import "./home.css";

import type { JSX } from "react";
import { lazy, Suspense } from "react";
import { useEffect, useRef, useState } from "react";
import { Route, Routes, useParams } from "react-router-dom";

import { CityIndex } from "./components/CityIndex";
import { CityPage } from "./components/CityPage";
import { Footer } from "./components/Footer";
import { Grain } from "./components/Grain";
import { Hero } from "./components/Hero";
import { LpRedirectRoute } from "./components/LpRedirectRoute";
// Static, not `lazy`: the home document is server-rendered and hydrated (see
// IndexBody), so a lazily resolved scene would be a chunk hop between the text
// and the planet, and MapLibre itself never enters this graph — the vendored ESM
// is a runtime path outside the bundle (MapSection), so what the split deferred
// was this scene's own ~20 KB, not the map.
import MapSection, { type MapHandle } from "./components/MapSection";
import { SkyBackground } from "./components/SkyBackground";
import { StarField } from "./components/StarField";
import { Unsubscribe } from "./components/Unsubscribe";
import { fetchLanding, type LandingData, readEmbeddedLanding } from "./lib/api";

type LoadStatus = "loading" | "ready" | "error";

const NokoPassCapturePage = lazy(async () => ({
  // oxlint-disable-next-line noko-lint/no-inline-import -- keep page-only locale data out of city SSR prerendering.
  default: (await import("./components/NokoPassCapture")).NokoPassCapture,
}));

function readUnsubscribeToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("token") || params.get("unsubscribe-token");
}

/**
 * The home directory for one payload: the whole scene, and the same tree the
 * server renders. The worker bakes this markup into the site's root object
 * (`renderIndexDocument`, entry-server) and the browser hydrates it, so two rules
 * hold here and nowhere else:
 *
 *   - nothing in this tree may read `window`/`document` while rendering, and
 *   - every reading it prints has to come from `data` — including the two that
 *     are otherwise "now": the row clocks and the hero's subtitle.
 *
 * `MapSection` is the one deliberate exception to "no client work": it builds the
 * globe in an effect, so its server render is the empty `.atlas-map` host and the
 * authored sphere stands in until the planet's first frame.
 */
export function IndexBody({
  data,
  status,
}: {
  data: LandingData | null;
  status: LoadStatus;
}): JSX.Element {
  const mapRef = useRef<MapHandle | null>(null);
  const sceneRef = useRef<HTMLDivElement>(null);

  const cities = data?.cities ?? [];
  const renderedAt = data?.generatedAt ?? null;
  const renderedAtMs = renderedAt === null ? null : Date.parse(renderedAt);

  return (
    <div className="landing-home">
      <a className="atlas-skip" href="#cities">
        SKIP TO CITIES
      </a>
      <main className="atlas-main">
        {/* One continuous scene: the sticky stage plus every beat that scrolls it,
            the closing section included, so the stage is still pinned at the
            page's last pixel. data-map-state / data-chapter are written by
            MapSection, so the authored fallback below is what the first paint
            shows. */}
        <div className="atlas-scene" ref={sceneRef}>
          <div className="atlas-stage">
            {/* noko.city orbital atlas: the authored stand-in for the MapLibre globe,
                shown from first paint until the real planet is ready (and on failure).
                A bare neutral sphere, latitude/longitude graticule only: no limb
                light, no coastlines, no country edges, no city dots, no external
                references.
                Inlined rather than a /public asset so it paints from the document
                alone, the failure path included. CSS sizes the disc from the viewBox,
                which is cropped to the r=360 disc so the painted sphere fills the box. */}
            <svg
              className="atlas-fallback"
              viewBox="40 40 720 720"
              aria-hidden="true"
              focusable="false"
            >
              <defs>
                <radialGradient id="atlas-body" cx="36%" cy="28%" r="82%">
                  <stop offset="0%" stopColor="#262833" />
                  <stop offset="46%" stopColor="#12131B" />
                  <stop offset="100%" stopColor="#07070C" />
                </radialGradient>
                <clipPath id="atlas-disc">
                  <circle cx="400" cy="400" r="360" />
                </clipPath>
              </defs>

              <circle cx="400" cy="400" r="360" fill="url(#atlas-body)" />

              <g clipPath="url(#atlas-disc)" fill="none" stroke="#C8C8D0" strokeWidth="1.1">
                <g strokeOpacity="0.2">
                  <ellipse cx="400" cy="400" rx="360" ry="108" />
                  <ellipse cx="400" cy="342" rx="312" ry="94" />
                  <ellipse cx="400" cy="458" rx="312" ry="94" />
                  <ellipse cx="400" cy="300" rx="180" ry="54" />
                  <ellipse cx="400" cy="500" rx="180" ry="54" />
                </g>
                <g strokeOpacity="0.15">
                  <ellipse cx="400" cy="400" rx="180" ry="360" />
                  <ellipse cx="400" cy="400" rx="312" ry="360" />
                  <ellipse cx="400" cy="400" rx="360" ry="360" />
                </g>
              </g>
            </svg>
            {/* Three thin orbital planes: the authored ellipse plus two more
                inclinations, all decorative. They are foreground planes at
                z-index 4, above the globe and its vignette, so each one draws as
                a single complete ellipse across land and water instead of losing
                the segments the planet covers — no collision maths, no mask and
                no front/back arc splitting. The satellites below ride the first
                and the third of them. */}
            <div className="atlas-ring" aria-hidden="true" />
            <div className="atlas-ring atlas-ring--second" aria-hidden="true" />
            <div className="atlas-ring atlas-ring--third" aria-hidden="true" />
            {/* ISS and Sputnik ride the inclined planes above, on two different
                tracks (-14deg and -42deg): MapSection writes each one's centre
                from the ring's own half-axes, so they sit exactly on the drawn
                ellipse. Flat atlas-palette fills and hairline strokes only — no
                glow, no filter, no asset fetch, no generated imagery — because a
                satellite has to stay identifiable at phone size. Both are
                upright as they travel: the artwork is never rotated. */}
            <div className="atlas-satellite atlas-satellite--iss" aria-hidden="true">
              <svg viewBox="0 0 100 60" aria-hidden="true" focusable="false">
                <g fill="#242632" stroke="#505565" strokeWidth="0.8">
                  <rect x="2" y="27" width="96" height="6" rx="1" />
                  <rect x="8" y="5" width="26" height="16" />
                  <rect x="8" y="39" width="26" height="16" />
                  <rect x="66" y="5" width="26" height="16" />
                  <rect x="66" y="39" width="26" height="16" />
                </g>
                <g fill="#0D111A" stroke="#505565" strokeWidth="0.9">
                  <rect x="44" y="13" width="12" height="12" rx="2" />
                  <rect x="44" y="35" width="12" height="12" rx="2" />
                </g>
                <g stroke="#505565" strokeWidth="0.5" strokeOpacity="0.9">
                  <path d="M 16.7 5 V 21 M 25.3 5 V 21 M 8 13 H 34" />
                  <path d="M 16.7 39 V 55 M 25.3 39 V 55 M 8 47 H 34" />
                  <path d="M 74.7 5 V 21 M 83.3 5 V 21 M 66 13 H 92" />
                  <path d="M 74.7 39 V 55 M 83.3 39 V 55 M 66 47 H 92" />
                </g>
                <path d="M 2 30 H 98" stroke="#C8C8D0" strokeWidth="0.5" strokeOpacity="0.5" />
                <ellipse
                  cx="50"
                  cy="30"
                  rx="6"
                  ry="3.4"
                  fill="#242632"
                  stroke="#505565"
                  strokeWidth="0.8"
                />
              </svg>
            </div>
            <div className="atlas-satellite atlas-satellite--sputnik" aria-hidden="true">
              <svg viewBox="0 0 80 80" aria-hidden="true" focusable="false">
                <g stroke="#C8C8D0" strokeWidth="1.2" strokeLinecap="round">
                  <path d="M 32 32 L 74 62" />
                  <path d="M 30 35 L 60 74" />
                  <path d="M 34 29 L 76 46" />
                  <path d="M 27 38 L 40 76" />
                </g>
                <circle cx="24" cy="26" r="11" fill="#242632" stroke="#505565" strokeWidth="0.9" />
                <circle cx="20" cy="22" r="3.6" fill="#0D111A" stroke="#505565" strokeWidth="0.5" />
                <path
                  d="M 15.4 19.4 A 11 11 0 0 1 24 15"
                  fill="none"
                  stroke="#C8C8D0"
                  strokeWidth="0.8"
                  strokeOpacity="0.5"
                />
              </svg>
            </div>
            {/* Stars: the static gradient field plus the twinkle points, one
                shared implementation with the city guide's stage. Same layer,
                so the planet occludes both identically. */}
            <StarField />
            <div className="atlas-chapters" aria-hidden="true">
              <span className="atlas-chapter" data-chapter="orbit">
                01 / ORBIT
              </span>
              <span className="atlas-chapter" data-chapter="approach">
                02 / CLOSER TO HOME
              </span>
              <span className="atlas-chapter" data-chapter="cities">
                03 / FIND YOUR CITY
              </span>
            </div>
            {/* Closing finale: the moon is a second, lazily constructed MapLibre
                globe rendering the real LROC maria outlines from the committed
                /vendor/moon/mare.json — a sibling of the Earth, not the flat
                illustration it replaces. Its centre and size are written inline
                by the body renderer off the closing progress, and its face stays
                locked to the Tycho meridian, so the coming-soon caption never
                rotates out of view; a MapLibre Marker still projects it onto
                Tycho's real lunar coordinates rather than pinning it at a fixed
                percentage. No asset is requested and no second map is built until
                the finale is entered, and the stage's own overflow still clips
                the offscreen entrance. Decorative, so the whole figure is out of
                the accessibility tree; `data-moon-state` keeps the host invisible
                until a globe-projected frame has rendered, and there is no flat
                stand-in by design. */}
            <figure className="atlas-moon" aria-hidden="true">
              <div className="atlas-moon-map" />
            </figure>
            <MapSection cities={cities} mapRef={mapRef} sceneRef={sceneRef} />
          </div>
          <Hero seed={renderedAt} />
          <CityIndex
            cities={cities}
            status={status}
            renderedAt={renderedAtMs}
            viewerCountry={data?.viewerCountry}
            onHoverCity={(name) =>
              name === null ? mapRef.current?.releaseHighlight() : mapRef.current?.flyTo(name)
            }
          />
          {/* The finale lives inside the scene so the stage above stays pinned
              through it: no opaque panel, the Earth keeps its frame and the
              footer still lands on the page's last line. */}
          <section className="atlas-closing" data-atlas-beat="closing">
            <h2 className="atlas-display">
              <span className="atlas-display-line">SEE YOU</span>
              <span className="atlas-display-line">OUT THERE.</span>
            </h2>
            <a className="atlas-cta" href="#cities">
              <span className="atlas-cta-bar" aria-hidden="true" />
              <span className="atlas-cta-label">FIND YOUR CITY</span>
            </a>
            <div className="atlas-footer">
              <Footer />
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

/**
 * `/` in the browser: read the payload the document shipped and render the scene
 * body above from it. The payload is the whole reason `/` needs no request — the
 * worker embeds it in the site's root object (see IndexBody) — so the only fetch
 * is a dev-server document, which has none.
 */
function IndexRoute(): JSX.Element {
  const [baked] = useState(readEmbeddedLanding);
  const [data, setData] = useState<LandingData | null>(baked);
  const [status, setStatus] = useState<LoadStatus>(baked ? "ready" : "loading");
  // Read after mount, never during the first render: the document this route
  // loaded is the home document, so the first client render must be the tree the
  // server baked or hydration throws that markup away. The unsubscribe view takes
  // over a beat later instead.
  const [unsubscribeToken, setUnsubscribeToken] = useState<string | null>(null);

  useEffect(() => {
    setUnsubscribeToken(readUnsubscribeToken());
  }, []);

  useEffect(() => {
    if (unsubscribeToken || baked) return undefined;
    // A built document carries the payload only on `/`. The router has already
    // pushed "/" by the time this runs, so the document itself is the sign that a
    // guide SPA-navigated into the directory (its wordmark and every ALL CITIES
    // link do that): reload to get the real home. A payload-less `/` keeps the
    // failure state, which is also what stops a stale one reload-looping.
    if (!import.meta.env.DEV) {
      if (document.getElementById("noko-city-page")) {
        window.location.replace("/");
        return undefined;
      }
      setStatus("error");
      return undefined;
    }
    const controller = new AbortController();
    void (async () => {
      try {
        setData(await fetchLanding(controller.signal));
        setStatus("ready");
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        // Never fabricate a successful empty payload: the directory owns the error state.
        setStatus("error");
      }
    })();
    return () => controller.abort();
  }, [baked, unsubscribeToken]);

  if (unsubscribeToken) {
    return (
      <div className="relative min-h-screen">
        <SkyBackground />
        <Grain />
        <Unsubscribe token={unsubscribeToken} />
      </div>
    );
  }

  return <IndexBody data={data} status={status} />;
}

function CityRoute(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  return <CityPage slug={slug ?? ""} />;
}

function NokoPassCaptureRoute(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const captureToken = token ?? "";
  return (
    <Suspense
      fallback={
        <output className="block p-8 font-mono text-sm text-paper-muted" aria-live="polite">
          Loading Noko Pass form…
        </output>
      }
    >
      <NokoPassCapturePage key={captureToken} token={captureToken} />
    </Suspense>
  );
}

/**
 * `/city/<slug>` is the legacy guide URL. In production a viewer-request
 * CloudFront function issues the 301 to `/page/<slug>` before the SPA loads;
 * this keeps `vite dev`/`preview` and any cached client bundle on the same
 * canonical URL — as the document load the redirect is, never as an in-app
 * navigation, because no SPA route renders a guide body (`lib/guide-document.ts`).
 * `replace`, not `assign`, so Back does not re-enter the redirect.
 */
function LegacyCityRoute(): JSX.Element | null {
  const { slug } = useParams<{ slug: string }>();
  window.location.replace(`/page/${slug ?? ""}${window.location.search}${window.location.hash}`);
  return null;
}

export default function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<IndexRoute />} />
      <Route path="/page/izmir/lp" element={<LpRedirectRoute />} />
      <Route path="/city/izmir/lp" element={<LpRedirectRoute />} />
      <Route path="/noko-pass/capture/:token" element={<NokoPassCaptureRoute />} />
      <Route path="/page/:slug" element={<CityRoute />} />
      <Route path="/city/:slug" element={<LegacyCityRoute />} />
    </Routes>
  );
}
